import { z } from 'zod';
import { idParamSchema } from './common';
import type { PaymentMethod, SessionEndReason } from './enums';

/**
 * The end-of-shift screen and the submissions staff make from it.
 *
 * Everything here is attributed to a person: a visit to whoever registered it,
 * a payment to whoever took it, a completed report to whoever performed it. A
 * login session is the window those are counted over, and a submission is a
 * staff member's signed-off snapshot of their own session.
 */

/** Free text a person typed into a box — explained, not essayed. */
export const DAY_CLOSE_REMARK_MAX_LENGTH = 2000;

const remark = z
  .string()
  .trim()
  .max(DAY_CLOSE_REMARK_MAX_LENGTH, 'Keep it under 2,000 characters')
  .nullish();

/**
 * `user_id` narrows the day to one staff member. Only an admin may choose it;
 * for anyone else the server scopes to the caller and echoes that back.
 */
export const dayCloseQuerySchema = z.object({
  on: z.iso.date().optional(),
  user_id: idParamSchema.optional(),
});
export type DayCloseQuery = z.infer<typeof dayCloseQuerySchema>;

export const submitDayCloseSchema = z.object({
  /** Why the drawer does not match, or anything else the admin should know. */
  comment: remark,
});
export type SubmitDayCloseInput = z.infer<typeof submitDayCloseSchema>;

export const reviewDayCloseSchema = z.object({
  note: remark,
});
export type ReviewDayCloseInput = z.infer<typeof reviewDayCloseSchema>;

interface StaffRef {
  id: number;
  first_name: string;
  last_name: string | null;
}

/** One window's figures — a calendar day, or one login session. */
export interface DayCloseFigures {
  registrations: number;
  /**
   * `Patient.total` of the visits registered in the window, which includes
   * sales tax — so it is not all revenue. `tax_collected` is shown beside it
   * rather than subtracted: the drawer is counted against what was actually
   * taken.
   */
  billed: number;
  tax_collected: number;
  /** What those visits still owe. */
  outstanding: number;
  /**
   * Money taken in the window, net of refunds — **by when it was taken**, not
   * by when the visit was registered. That is what the drawer holds, and why it
   * can exceed `billed`: a balance collected today on last week's visit.
   */
  collected: number;
  /** `collected`, split by how it arrived. Null is imported history. */
  collected_by_method: { method: PaymentMethod | null; amount: number }[];
  /** The part of `collected` paid against visits registered before the window. */
  collected_on_earlier_visits: number;
  /** Reports completed in the window, whenever they were registered. */
  completed: number;
  /** Reports registered in the window and still with nothing entered. */
  not_started: number;
}

/** What a staff member declared at submit time. */
interface DayCloseSnapshot {
  registrations: number;
  billed: number;
  tax: number;
  collected: number;
  outstanding: number;
}

export interface DayCloseSubmissionView {
  id: number;
  submitted_at: string;
  comment: string | null;
  snapshot: DayCloseSnapshot;
  reviewed_at: string | null;
  reviewed_by: StaffRef | null;
  review_note: string | null;
}

/**
 * `open` is still signed in; `submitted` ended with a Day Close; `missing`
 * ended any other way — signed out without submitting, signed in elsewhere, or
 * left to expire. `end_reason` says which.
 */
export type DayCloseSessionStatus = 'open' | 'submitted' | 'missing';

export interface DayCloseSessionRow {
  id: number;
  user: StaffRef;
  started_at: string;
  ended_at: string | null;
  end_reason: SessionEndReason | null;
  status: DayCloseSessionStatus;
  /** The session's figures as they stand now — compare with the snapshot. */
  live: { registrations: number; billed: number; collected: number };
  submission: DayCloseSubmissionView | null;
}

export interface DayClose extends DayCloseFigures {
  on: string;
  /** Whose figures these are, after scoping: a staff member, or null for everyone. */
  user_id: number | null;
  /** Visits with a balance, oldest first — the front desk's chase list. */
  unpaid: { patient_id: number; name: string; pin_no: string; balance: number }[];
  /** Every login overlapping the day, oldest first, with its submission. */
  sessions: DayCloseSessionRow[];
}

/** `GET /day-close/session` — what the sign-out dialog asks the user to confirm. */
export interface CurrentSessionClose {
  session: { id: number; started_at: string };
  figures: DayCloseFigures;
}
